import { useState, useEffect } from 'react';
import { ColourFinish } from '../types';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

export interface ColourCombination {
  id: string;
  fid: string | null;
  name: string | null;
  brand: string | null;
  colours: string[];
  color_description: string | null;
  finish_type: string;
  blend_axis?: 'horizontal' | 'vertical' | null;
  blend_mode?: 'full' | 'centered' | null;
  blend_center?: number | null;
  blend_window?: number | null;
  blend_space?: 'srgb' | 'oklab' | null;
  blend_seam_offset?: number | null;
  blend_steepness?: number | null;
  is_visible?: boolean; // NEW: Visibility field
  created_at?: string;
  updated_at?: string;
}

// Helper function to parse color_description into individual color names
const parseColorNames = (colorDescription: string | null): string[] => {
  if (!colorDescription) return [];
  return colorDescription
    .split('/')
    .map(color => color.trim())
    .filter(color => color.length > 0);
};

export function useColours() {
  const [combinations, setCombinations] = useState<ColourCombination[]>([]);
  const [availableColours, setAvailableColours] = useState<string[]>([]);
  const [selectedFinish, setSelectedFinish] = useState<ColourFinish>('shifting');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchColours();
  }, [selectedFinish]);

  const fetchColours = async () => {
    try {
      setLoading(true);
      setError(null);

      // NEW: Only fetch visible combinations for frontend users
      // Check if user is authenticated to determine if they should see all combinations
      const { data: { session } } = await supabase.auth.getSession();

      let query = supabase
        .from('colour_combinations')
        .select('*')
        .eq('finish_type', selectedFinish);

      // If user is not authenticated (public user), only show visible combinations
      if (!session) {
        query = query.eq('is_visible', true);
      }

      const { data: combinations, error: combError } = await query;

      if (combError) {
        throw combError;
      }

      // Extract unique color names from color_description fields
      const uniqueColours = Array.from(
        new Set(
          (combinations || []).flatMap(combo => parseColorNames(combo.color_description))
        )
      ).sort();

      setAvailableColours(uniqueColours);
      setCombinations(combinations || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching colours';
      setError(errorMessage);
      toast.error(errorMessage);
      setAvailableColours([]);
      setCombinations([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCombinations = (selectedColours: string[]) => {
    if (!selectedColours.length) return combinations;
    return combinations.filter(combo => {
      const comboColorNames = parseColorNames(combo.color_description);
      return selectedColours.every(colour => comboColorNames.includes(colour));
    });
  };

  const getAvailableColours = (selectedColours: string[]) => {
    if (!selectedColours.length) return availableColours;

    // Get all combinations that include the selected colours
    const validCombinations = filterCombinations(selectedColours);

    // Extract all unique color names from these combinations
    const validColours = Array.from(
      new Set(
        validCombinations.flatMap(combo => parseColorNames(combo.color_description))
      )
    ).sort();

    // Remove already selected colours
    return validColours.filter(colour => !selectedColours.includes(colour));
  };

  // NEW: Helper function to get visibility stats (useful for admin components)
  const getVisibilityStats = () => {
    const total = combinations.length;
    const visible = combinations.filter(c => c.is_visible === true).length;
    const hidden = total - visible;
    return { total, visible, hidden };
  };

  // NEW: Helper function to toggle visibility (admin only)
  const toggleCombinationVisibility = async (combinationId: string, isVisible: boolean) => {
    try {
      const { error } = await supabase
        .from('colour_combinations')
        .update({ is_visible: isVisible, updated_at: new Date().toISOString() })
        .eq('id', combinationId);

      if (error) throw error;

      // Update local state
      setCombinations(prev => 
        prev.map(combo => 
          combo.id === combinationId 
            ? { ...combo, is_visible: isVisible }
            : combo
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error updating visibility:', error);
      return { success: false, error };
    }
  };

  return {
    combinations,
    availableColours: getAvailableColours,
    loading,
    error,
    setSelectedFinish,
    filterCombinations,
    getVisibilityStats, // NEW: For admin components
    toggleCombinationVisibility, // NEW: For admin components
    refetch: fetchColours // NEW: Allow manual refetch
  };
}
